import React, { useState, useEffect } from 'react';
import { PrimaryButton, SecondaryButton } from "../UI";
import ConfigureExperience from '../Experience/ConfigureExperience';
import ChooseTemplate from '../Template/ChooseTemplate';
import tickImg from '../../assets/tick-step.svg';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { createEntryByPath, createExperience, createTenantActivityOffers, delinkOfferFromExperience, editExperience, editExperienceOfferMapping, getEntryByPath, getOfferExperienceMappings, updateEntryByPath } from '../../utils/APIs';
import { useUserContext } from '../../contexts/UserContext';
import Loader from '../UI/Loader/Loader';
import { useLocation, useNavigate } from 'react-router-dom';
import { compareJson, getObjFromHtml, isValidJsonString } from '../../utils/helper';
import SuccessAlert from '../UI/Alert/SuccessAlert';
import Input from '../UI/Input';
import { permission } from '../../config/permission';
import OfferCode from '../Offers/offerCode';
import CreateOfferAndExperienceModal from '../ManageAEMContentFragments/CreateOfferAndExperienceModal';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import Activation from '../ExperienceCode/Activation';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import DropdownButton from './DropdownButton';
import UpdateGithubEntryAssociationModal from './UpdateGithubEntryAssociationModal';

const GithubEntryDetails = () => {
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const id = query.get("id");
    const navigate = useNavigate();
    const { enums, content: githubData } = useContentfulContext();
    const { enablePreviewMode, disablePreviewMode } = usePreviewMode();

    const [currentStep, setCurrentStep] = useState('choose-template');
    const [isStepperHidden, setIsStepperHidden] = useState(false);
    const [githubEntryName, setGithubEntryName] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { userData, checkPermission } = useUserContext();
    const [selectedTemplate, setSelectedTemplate] = useState({});
    const [fieldValues, setFieldValues] = useState();
    const [dynamicFields, setDynamicFields] = useState({});
    const [transformedExperienceJSON, setTransformedExperienceJSON] = useState();
    const [githubEntry, setGithubEntry] = useState()
    const [originalGithubEntry, setOriginalGithubEntry] = useState();
    const [validationError, setValidationError] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const [showCreateOfferModal, setShowCreateOfferModal] = useState();
    const [type, setType] = useState('');
    const [experience, setExperience] = useState(null);

    const [showUpdateOfferModal, setShowUpdateOfferModal] = useState(false);
    const [associatedExperiences, setAssociatedExperiences] = useState([]);

    useEffect(() => {
      getGithubEntry()
      getEntryExpMappings()
    }, [])


    async function getEntryExpMappings() {
      try {
        const mappings = await getOfferExperienceMappings(
          userData?.token,
          userData?.selectedOrganization?.org_token,
          id,
          userData?.defaultGithub?._id
        );
        setAssociatedExperiences(mappings?.data || []);
      } catch (error) {
        console.log(error)
      }
    }

    useEffect(() => {
      const timer = setTimeout(() => {
        setError('');
        setSuccessMessage('');
      }, 4000);
  
      return () => clearTimeout(timer);
    }, [error, successMessage]);


  const getGithubEntry = async () => {
    try {
      if(!userData?.defaultGithub) {
        toast.error(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.no_github_entry_found);
        return;
      }
      setLoading(true);
      const result = await getEntryByPath({
        token: userData?.token,
        organizationId: userData?.selectedOrganization?.org_token,
        github_integration_id: userData?.defaultGithub?._id,
        path: id
      });

      if (!result?.data?.content) {
        toast.error("Invalid Github entry: content not found");
        navigate("/experience/github-entries");
        return;
      }

      const contentData = result?.data?.content;
      const name = id;

      setGithubEntry(contentData);
      setOriginalGithubEntry(contentData);
      setGithubEntryName(name);
      setType(isValidJsonString(contentData) ? "json" : "content");

    } catch (error) {
      toast.error(error?.message || githubData?.COMMON_MESSAGE.something_wrong_msg)
    } finally {
      setLoading(false);
    }
  }

    const handleUpdateGithubEntry = async (showLoader = true) => {
      try {
        if (!githubEntryName) {
          setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.name_required)
          return;
        }
        if (validationError) return;

        const isValidate = handleValidateJson();
        if (isValidate.length > 0) return;

        if(showLoader) setLoading(true)

        const raw = githubEntry?.content ?? githubEntry;
        const experienceJson = selectedTemplate.type !== enums.templateTypes.json_a_to_html.id
          ? JSON.stringify(transformOfferJSON())
          : raw;

        const res = await updateEntryByPath({
          token: userData?.token,
          organizationId: userData?.selectedOrganization?.org_token,
          data: {
            path: id,
            github_integration_id: userData.defaultGithub?._id,
            content: experienceJson,
          }
        });
        setSuccessMessage(res?.message || githubData?.GITHUB_ENTRY_DETAILS_CONTENT?.success?.updated);
      } catch (error) {
        console.log('error', error)
        setError(error?.message || githubData?.COMMON_MESSAGE.something_wrong_msg);
      } finally {
        if(showLoader) setLoading(false)
      }
    };

    function getFlattenExperienceJSON(ob) {
      let result = {};
      function recurse(cur, prop) {
        if (Object(cur) !== cur) {
          if (dynamicFields[prop] !== undefined) {
            result[prop] = fieldValues?.[prop];
            setDynamicFields((prev) => ({ ...prev, [prop]: cur }));
          } else {
            result[prop] = cur;
          }
        } else if (Array.isArray(cur)) {
          for (let i = 0; i < cur.length; i++) {
            recurse(cur[i], prop ? prop + "[" + i + "]" : i);
          }
          if (cur.length === 0) result[prop] = [];
        } else {
          let isEmpty = true;
          for (let p in cur) {
            isEmpty = false;
            recurse(cur[p], prop ? prop + "." + p : p);
          }
          if (isEmpty && prop) result[prop] = {};
        }
      }
      recurse(ob, "");
      return result;
    }

    const createOffer = async (values) => {
      try {
        if(!githubEntryName) {
          setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.name_required)
          return;
        }
        if(validationError) return;

        if(showPreview) {
          const isValidate = handleValidateJson();
          if (isValidate.length > 0) return;
        }
        const raw = githubEntry?.content ?? githubEntry;
        const content = isValidJsonString(raw) ? JSON.parse(raw) : raw;

        const payload = { 
          tenant: userData.defaultAdobeIO?.tenant_name,
          data: {
            name: values.offer_name,
            workspace: userData.defaultWorkspace?.workspace_id,
            content: (showPreview && selectedTemplate?._id && selectedTemplate.type !== enums.templateTypes.json_a_to_html.id) ? transformOfferJSON() : content,
            type: selectedTemplate?.type === enums.templateTypes.json_a_to_html.id ? 'content' : (type || 'json'),
          }
        }
        const result = await createTenantActivityOffers(
          userData?.token,
          userData?.selectedOrganization?.org_token,
          payload
        )
        return result;
      } catch (error) {
        throw error;
      }
    };

    const createExperienceHandler = async (values, offerId) => {
      try {
          if(!selectedTemplate) {
            setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.template_required);
            return;
          }

          if(showPreview) {
            const isValidate = handleValidateJson();
            if (isValidate.length > 0) {
              setError(isValidate.join(", \n"))
              return
            }
          }

          if(validationError) {
              setError(validationError);
              return
          }
          
          const raw = githubEntry?.content ?? githubEntry;
          const experienceJson = (showPreview && (selectedTemplate.type !== enums.templateTypes.json_a_to_html.id))
          ? JSON.stringify(transformOfferJSON())
          : raw;

          const payload = {
              name: values.experience_name,
              templates: selectedTemplate?._id ? [selectedTemplate._id] : [],
              html: '',
              css: '',
              config_json: '',
              experience_json: experienceJson,
              source: enums.experience_sources.offer_page.id
          }
          const result = await createExperience(
              userData?.token,
              userData?.selectedOrganization?.org_token,
              payload
          );
        if (result.data && result.data?.[0]._id) {
          const offerMappingPayload = {
            experience_id: result.data?.[0]._id,
            id: id,
            integration_id: userData.defaultGithub?._id,
            destination: "github"
          };
          await editExperienceOfferMapping(userData?.token, userData?.selectedOrganization?.org_token, offerMappingPayload);
        }
          setExperience(result?.data)
          return result;
      } catch (error) {
        throw error;
      }
  }

  const handleSaveAsNewEntry = async (path) => {
    try {
      if (!path) {
        toast.error("Please provide a valid file path for the new entry");
        return;
      }
      setLoading(true);
      const raw = githubEntry?.content ?? githubEntry;
      const code = (showPreview && (selectedTemplate.type !== enums.templateTypes.json_a_to_html.id))
        ? JSON.stringify(transformOfferJSON())
        : isValidJsonString(raw)
          ? JSON.parse(raw)
          : raw;

      const payload = {
        token: userData?.token,
        organizationId: userData?.selectedOrganization?.org_token,
        data: {
          github_integration_id: userData?.defaultGithub?._id,
          path,
          content:
            typeof code === "string" && isValidJsonString(code)
              ? JSON.parse(code)
              : code,
        },
      };
      const result = await createEntryByPath(payload);

      if (result?.status) {
        toast.success(result.message || "New Github entry created successfully")
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong";

      toast.error(errorMessage);
    }
    finally {
      setLoading(false);
    }
  };

  const handleUpdateAssociatedExperiences = async () => {
    if (associatedExperiences.length <= 0) {
      toast.error("No associated experiences to update.");
      return;
    }
    try {
      const raw = githubEntry?.content ?? githubEntry;
      const experienceJson = (showPreview && (selectedTemplate.type !== enums.templateTypes.json_a_to_html.id))
        ? JSON.stringify(transformOfferJSON())
        : raw;
      setLoading(true);
      await Promise.all(
        associatedExperiences.map(exp => {
          const payload = {
            name: exp.name,
            templates: selectedTemplate?._id ? [selectedTemplate._id] : [],
            html: '',
            css: '',
            config_json: '',
            experience_json: experienceJson,
            version: exp.published_version  // for updating published version which can update all associated activations
          };
          return editExperience(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            exp.experience_id,
            userData?.defaultAdobeIO?._id,
            userData?.defaultContentful?._id,
            userData?.defaultTealiumKey?._id,
            payload
          );
        })
      );
      toast.success("Associated experiences updated successfully");
    } catch (err) {
      toast.error(err?.message || "Failed to update associated experiences");
    } finally {
      setLoading(false);
    }
  };

  const handleDelinkExperience = async () => {
    if (associatedExperiences.length <= 0) {
      toast.error("No associated experiences to delink.");
      return;
    }
    try {
      setLoading(true);
      await Promise.all(
        associatedExperiences.map(exp =>
          delinkOfferFromExperience(
            userData?.token,
            userData?.selectedOrganization?.org_token,
            {
              experience_id: exp.experience_id,
              id: id,
              integration_id: userData?.defaultGithub?._id,
              destination: "github",
            }
          )
        )
      );
      setAssociatedExperiences([]);
      await handleUpdateGithubEntry(false)
      toast.success("Offer delinked from associated experiences");
    } catch (err) {
      toast.error(err?.message || "Failed to delink offer");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentStep == 'choose-template' && selectedTemplate._id) {
      if (!isValidJsonString(selectedTemplate?.json_schema)) {
        setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.valid_json);
        return;
      }

      let experienceJson = isValidJsonString(githubEntry) ? JSON.parse(githubEntry) : githubEntry;

      // Convert HTML → JSON only for json_a_to_html
      if (selectedTemplate.type === enums?.templateTypes.json_a_to_html.id) {
        const parsedJsonSchema = JSON.parse(selectedTemplate?.json_schema);
        const updatedExperienceJson = getObjFromHtml(originalGithubEntry, parsedJsonSchema?.config_obj_key);
        if (!updatedExperienceJson) {
          setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.config_obj_not_found);
          return;
        }
        experienceJson = updatedExperienceJson;
      }

      const temp = JSON.parse(selectedTemplate?.json_schema);
      delete temp?.config_obj_key;

      const compare = compareJson(temp, experienceJson);
      if (compare.length == 0 || (compare.length == 1 && compare.includes("experience_id"))) {
        let flattenJSON = getFlattenExperienceJSON(experienceJson);
        let temps = {};
        for (const key in flattenJSON) {
          const element = JSON.parse(selectedTemplate.config_json).find((x) => x.id === key);
          if (element?.input?.input_type === "dynamic-text") {
            temps[element.id] = flattenJSON[key];
            flattenJSON[key] = element.input?.input_default_value;
          }
        }
        setGithubEntry(experienceJson);
        setDynamicFields(temps);
        setFieldValues(flattenJSON);
      } else {
        setError(`${compare.join(", ")}${githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.templates_not_same}`);
        return;
      }

      if (currentStep == 'choose-template') {
        setCurrentStep('configure-offer');
      }
    }
  }, [selectedTemplate]);

  const handleNext = () => {
    if (currentStep == 'choose-template' && !selectedTemplate._id) {
      setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.template_required);
      return;
    }

    if (currentStep == 'choose-template' && selectedTemplate._id) {
      if (!isValidJsonString(selectedTemplate?.json_schema)) {
        setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.valid_json);
        return;
      }

      let experienceJson = isValidJsonString(githubEntry) ? JSON.parse(githubEntry) : githubEntry;

      if (selectedTemplate.type === enums?.templateTypes.json_a_to_html.id) {
        const parsedJsonSchema = JSON.parse(selectedTemplate?.json_schema);
        const updatedExperienceJson = getObjFromHtml(originalGithubEntry, parsedJsonSchema?.config_obj_key);
        if (!updatedExperienceJson) {
          setError(githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.config_obj_not_found);
          return;
        }
        experienceJson = updatedExperienceJson;
      }

      const temp = JSON.parse(selectedTemplate?.json_schema);
      delete temp?.config_obj_key;

      const compare = compareJson(temp, experienceJson);
      if (compare.length == 0) {
        let flattenJSON = getFlattenExperienceJSON(experienceJson);
        let temps = {};
        for (const key in flattenJSON) {
          const element = JSON.parse(selectedTemplate.config_json).find((x) => x.id === key);
          if (element?.input?.input_type === "dynamic-text") {
            temps[element.id] = flattenJSON[key];
            flattenJSON[key] = element.input?.input_default_value;
          }
        }
        setGithubEntry(experienceJson);
        setDynamicFields(temps);
        setFieldValues(flattenJSON);
      } else {
        setError(`${compare.join(", ")}${githubData.GITHUB_ENTRY_DETAILS_CONTENT.errors.templates_not_same}`);
        return;
      }
    }

    if (currentStep == 'choose-template') {
      setCurrentStep('configure-offer');
    } else if (currentStep == 'configure-offer') {
      setCurrentStep('update-offer');
    }
  }

    function transformOfferJSON() {
      const transformedJSON = {};
      for (const key in fieldValues) {
        const keys = key.split('.');
        keys.reduce((acc, part, index) => {
          if (part.includes('[')) {
            const [arrayKey, arrayIndex] = part.split(/\[|\]/).filter(Boolean);
            if (!acc[arrayKey]) {
              acc[arrayKey] = [];
            }
            if (!acc[arrayKey][arrayIndex]) {
              acc[arrayKey][arrayIndex] = {};
            }
            if (index === keys.length - 1) {
              // acc[arrayKey][arrayIndex] = fieldValues[key];
              if(dynamicFields[key] !== undefined) {
                acc[arrayKey][arrayIndex] = dynamicFields[key];
              } else {
                acc[arrayKey][arrayIndex] = fieldValues[key];
              }
            }
            return acc[arrayKey][arrayIndex];
          } else {
            if (index === keys.length - 1) {
              // acc[part] = fieldValues[key];
              if(dynamicFields[key] !== undefined) {
                acc[part] = dynamicFields[key];
              } else {
                acc[part] = fieldValues[key];
              }
            } else {
              if (!acc[part]) {
                acc[part] = {};
              }
              return acc[part];
            }
          }
        }, transformedJSON);
      }
      return transformedJSON;
    }

  const handleValidateJson = () => {
    setValidationError("");

    const config_elems = [];
    JSON.parse(selectedTemplate.config_json).forEach((el) => {
      el.error = "";
      config_elems.push({ ...el, id: el.id });
    });
    const newConfigElems = [...config_elems];
    const errs = [];

    for (let item of newConfigElems) {
      let val = "";
      if (dynamicFields[item.id] !== undefined) {
        val = dynamicFields[item.id]
      } else {
        val = fieldValues[item.id];
      }

      const rules = item.rules;
      if (rules.is_mandatory) {
        if (val?.length == 0) {
          const message = "Field is required";
          item.error = message;
          errs.push(`${item.id}: ${message}`)
        } else {
          item.error = "";
        }
      }
      if (rules.min_length && rules.max_length && item.error.length == 0) {
        if (val.length > rules.max_length || val.length < rules.min_length) {
          const message = `Min length: ${rules.min_length} and Max length: ${rules.max_length}`;
          item.error = message;
          errs.push(`${item.id}: ${message}`);
        } else {
          item.error = "";
        }
      }
    }

    if (errs.length > 0) {
      setValidationError(errs.join(", \n"));
    }
    return errs;
  }

    const handleSubmit = async (action, values) => {
      try {
        setError('');
        setLoading(true);
        let successMsg, result;
        switch (action) {
          case githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.id:
            if(!values?.offer_name) {
              setError("Offer name is required.")
              return;
            }
            result = await createOffer(values);
            successMsg = `${githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.success}. Offer Id - ${result.data.id}, Workspace Name: ${userData.defaultWorkspace?.workspace_name}`;
            break;

          case githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.id:
            if(!values?.experience_name) {
              setError("Experience name is required.")
              return;
            }
            result = await createExperienceHandler(values);
            successMsg = githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.success
            break;        
          default:
            break;
        }

        if(result) {
          setSuccessMessage(successMsg);
          setTimeout(() => {
            navigate("/experience/github-entries")
          }, 1500);
        }
      } catch (error) {
        console.log('error', error)
        setError(error?.message || githubData?.COMMON_MESSAGE.something_wrong_msg);
      } finally {
        setLoading(false);
      }
    };

    const handleAction = (action) => {
      switch (action) {
        case githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.update_github.id:
          setShowUpdateOfferModal(true)
          // handleUpdateGithubEntry()
          break;

        case githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.id:
          setCurrentStep("activate")
          break;
        case githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.id:
          setShowCreateOfferModal({
            action,
            fields: [githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.save_experience.id]
          })
          break;
        default:
          console.log(`Unknown action: ${action}`);
      }
    }

    const handleCloseModal = () => {
      setShowCreateOfferModal(null)
    }

    useEffect(() => {
      if(currentStep == "configure-offer") {
        enablePreviewMode();
      } else {
        disablePreviewMode();
      }
    }, [currentStep])

    return (
        <div className='w-full mx-auto overflow-auto sticky z-[15]'>
            { loading && <Loader /> }
            { error && <ErrorAlert timeout="3000">{error}</ErrorAlert> }
            { successMessage && <SuccessAlert disappearTime={4000}>{successMessage}</SuccessAlert> }
            {/* Back Link */}
            {!showPreview && currentStep !== 'activate' && (<div className="w-full px-8 pt-4 pb-2">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/experience/github-entries");
                }}
                className="font-medium text-primary hover:underline flex items-center"
              >
                <img src={backwardArrow} alt="back" className="w-5 h-5 mr-2" />
                {githubData?.GITHUB_ENTRY_DETAILS_CONTENT?.back_cta_text2 || "Back to Github Entries"}
              </a>
            </div>)}
            <div className='py-2 px-4 md:px-8 bg-gray-1 mb-2 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4'>
                <div className='w-full md:max-w-[260px]'>
                    <Input
                        size="sm"
                        disabled
                        id="offer_name"
                        name="offer_name"
                        value={githubEntryName}
                        placeholder="Github Entry"
                    />
                </div>
                {!showPreview && !isStepperHidden && <div className='flex justify-end gap-3'>
                  {checkPermission(permission.AEM_CONTENT_FRAGMENT_OP_CREATE_PREVIEW) && <SecondaryButton size={'base'} handleClick={() => setShowPreview(true)}>{githubData.GITHUB_ENTRY_DETAILS_CONTENT.show_preview_cta}</SecondaryButton>}
                  {
                    checkPermission(permission.GITHUB_ENTRY_OP_OFFER) && <PrimaryButton size={'base'}
                      handleClick={() =>{
                        setCurrentStep("activate")
                        setIsStepperHidden(true)
                      }}
                    >
                      {githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.name}
                    </PrimaryButton>
                  }
                </div>}
                {showPreview && <ol className="flex items-center justify-center flex-1 text-xs font-medium text-center text-gray-500 gap-2 md:gap-0">
                    <li className="flex md:w-full items-center sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-gray-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10">
                        <button className="text-xs flex items-center flex-col after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200" onClick={() => {
                            setGithubEntry(originalGithubEntry)
                            setCurrentStep("choose-template");
                            setSelectedTemplate({})
                            setFieldValues()
                          }}
                        >
                            <img alt='' src={tickImg} className="w-4 h-4" />
                            <span className="hidden lg:block">{githubData.GITHUB_ENTRY_DETAILS_CONTENT.choose_template}</span>
                        </button>
                    </li>
                    <li className="text-xs flex items-center">
                        <button className="flex items-center flex-col" onClick={() => {
                          if(currentStep == "update-offer") {
                            setCurrentStep("configure-offer")
                          }
                        }}>
                            { (currentStep == 'configure-offer' || currentStep == 'update-offer' || currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">2</span> }
                            <span className="hidden lg:block">{githubData.GITHUB_ENTRY_DETAILS_CONTENT.configure}</span>
                        </button>
                    </li>
                    <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
                        <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
                            { (currentStep == 'update-offer' || currentStep == 'activate' ) ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">3</span> }
                            <span className="hidden lg:block">{githubData.GITHUB_ENTRY_DETAILS_CONTENT.update_github}</span>
                        </button>
                    </li>
                    <li className="text-xs flex md:w-full items-center before:content-[''] before:w-full before:h-1 before:border-b before:border-gray-200 before:border-1 before:hidden sm:before:inline-block before:mx-6 xl:before:mx-10">
                        <button className="flex items-center flex-col before:content-['/'] sm:before:hidden before:mx-2 before:text-gray-200">
                            { (currentStep == 'activate') ? <img alt='' src={tickImg} className="w-4 h-4" /> : <span className="w-4 h-4 flex items-center justify-center">4</span> }
                            <span className="hidden lg:block">{githubData.GITHUB_ENTRY_DETAILS_CONTENT.activate}</span>
                        </button>
                    </li>
                </ol>}
                {showPreview && (
                  <div className='flex justify-end gap-2 md:gap-3 w-full md:w-auto'>
                    {currentStep !== 'activate' && (
                      <SecondaryButton
                        size={'base'}
                        handleClick={() => navigate("/experience/github-entries")}
                      >
                        {githubData.GITHUB_ENTRY_DETAILS_CONTENT.cancel_btn}
                      </SecondaryButton>
                    )}
                    {currentStep == "update-offer" ? (
                      checkPermission(permission.EDIT_OFFER) && <DropdownButton handleSubmit={handleAction} />
                    ) : (currentStep !== "choose-template" && currentStep !== 'activate') ? (
                      <PrimaryButton size={'base'} handleClick={handleNext}>{githubData.GITHUB_ENTRY_DETAILS_CONTENT.next_btn}</PrimaryButton>
                    ) : null}
                        </div>
                      )}
            </div>
            <div className='h-full max-h-[calc(100vh-80px)] px-4 md:px-8'>
                { currentStep == 'choose-template' && githubEntry && (
                  <ChooseTemplate
                    title={githubData.GITHUB_ENTRY_DETAILS_CONTENT.title}
                    offerJson={{ content: isValidJsonString(githubEntry) ? JSON.parse(githubEntry) : githubEntry, isCreateOffer: true, offerType: "json" }}
                    selectedTemplate={selectedTemplate}
                    setSelectedTemplate={setSelectedTemplate}
                    hideTemplates={!showPreview}
                    type={type}
                  />
                )}
                { currentStep == 'configure-offer' && (
                  <ConfigureExperience
                    setCurrentStep={setCurrentStep}
                    selectedTemplate={selectedTemplate}
                    setSelectedTemplate={setSelectedTemplate}
                    fieldValues={fieldValues}
                    setFieldValues={setFieldValues}
                    setTransformedExperienceJSON={setTransformedExperienceJSON}
                    dynamicFields={dynamicFields}
                    setDynamicFields={setDynamicFields}
                    actionText={githubData.GITHUB_ENTRY_DETAILS_CONTENT.edit_github_entry}
                  />
                )}
                { currentStep == 'update-offer' && 
                  <OfferCode
                    title={githubData.GITHUB_ENTRY_DETAILS_CONTENT.title}
                    experienceJSON={transformedExperienceJSON}
                    selectedTemplate={selectedTemplate}
                    offerJson={githubEntry}
                    setOfferJson={setGithubEntry}
                    getFlattenExperienceJSON={getFlattenExperienceJSON}
                    setFieldValues={setFieldValues}
                    validationError={validationError}
                    setValidationError={setValidationError}
                    handleValidateJson={handleValidateJson}
                  />
                }
                { currentStep == 'activate' && 
                  <>
                    <h1 className="text-xl px-6 py-4 font-extrabold leading-tight tracking-tight text-gray-900 md:text-2xl">{githubData.GITHUB_ENTRY_DETAILS_CONTENT.dropdown_btn.activate_at.name}</h1>
                    <Activation
                      experience={experience}
                      setExperience={setExperience}
                      validationError={validationError}
                      setValidationError={setValidationError}
                      handleValidateJson={handleValidateJson}
                      offerPermission={{
                        create: permission.CREATE_EXP_OP_CREATE_OFFER
                      }}
                      contentfulPermission={{
                        create: permission?.CREATE_EXP_OP_CREATE_CONTENTFUL
                      }}
                      selectedTemplate={isStepperHidden ? null : selectedTemplate}
                      code={
                        showPreview
                          ? (
                            selectedTemplate.type === enums.templateTypes.json_a_to_html.id
                              ? (() => {
                                const raw = githubEntry?.content ?? githubEntry;
                                return isValidJsonString(raw) ? JSON.parse(raw) : raw;
                              })()
                              : transformOfferJSON()
                          )
                          : (() => {
                            const raw = githubEntry?.content ?? githubEntry;
                            return isValidJsonString(raw) ? JSON.parse(raw) : raw;
                          })()
                      }
                      offerActionConfig={{   /* passed config with create offer handler*/
                        handler : createOffer, /* call this handle for create offer if passed*/
                        source : "github"
                      }}
                      />
                  </>
                }
                {showCreateOfferModal && (
                  <CreateOfferAndExperienceModal
                    error={error}
                    title={'Github Entry'}
                    loading={loading}
                    handleSubmit={handleSubmit}
                    handleCloseModal={handleCloseModal}
                    offerSuccessMessage={successMessage}
                    showCreateOfferModal={showCreateOfferModal}
                  />
                )}

              {showUpdateOfferModal && (
                <UpdateGithubEntryAssociationModal
                  isOpen={showUpdateOfferModal}
                  onClose={() => setShowUpdateOfferModal(false)}
                  associatedExperiences={associatedExperiences}
                  onSaveAsNewOffer={(path) => handleSaveAsNewEntry(path)}
                  onUpdateAssociatedExperience={handleUpdateAssociatedExperiences}
                  onDelinkExperience={handleDelinkExperience}
                  loading={loading} 
                />
              )}
            </div>
        </div>
    )
}

export default GithubEntryDetails;